import constants from '../config/constants.js'

// Example usage
// let input =  '"jkl mno" pqr "stu vwx" yz';
// let output = getSplittedSearchString(input);
// console.log(output); // Output: [ 'jkl mno', 'pqr', 'stu vwx', 'yz' ]
const getSplittedSearchString = input => {
  const parts = input
    .split('+')
    .map(part => part.trim())
    .filter(Boolean)

  // Initialize an array to hold the final results
  const result = []

  // Process each part
  for (const part of parts) {
    // Match groups of non-space characters, or quoted strings with spaces
    const matches = part.match(/(?:[^\s"]+|"[^"]*")/g)

    if (matches) {
      for (const match of matches) {
        const cleanedMatch = match
          .replaceAll('"', '')
          .toLowerCase()
          .trim()

        result.push(cleanedMatch)
      }
    }
  }

  return result
    .filter(Boolean)
}

const getParticipantNameSearchSegments = (participant, options = { includeEmail: true }) => {
  if (!participant) {
    return []
  }

  const segments = []

  if (participant.user && options.includeEmail) {
    segments.push(participant.user.email)
  }

  if (participant.firstname && participant.lastname) {
    segments.push(`${participant.firstname} ${participant.lastname}`)
    segments.push(`${participant.lastname} ${participant.firstname}`)
    segments.push(`${participant.firstname[0]}. ${participant.lastname}`)
    segments.push(`${participant.lastname} ${participant.firstname[0]}.`)
    segments.push(`${participant.lastname[0]}. ${participant.firstname}`)
    segments.push(`${participant.firstname} ${participant.lastname[0]}.`)
  } else if (participant.firstname) {
    segments.push(participant.firstname)
  } else if (participant.lastname) {
    segments.push(participant.lastname)
  }

  return segments
    .filter(Boolean)
    .map(segment => segment.toLowerCase())
}

// A task number token like "task:#12" only matches a separately supplied exact
// search segment. A plain "#12" stays a substring match, so it still finds
// #120 and a "#12" written in a title, description or comment.
const TASK_NUMBER_SEARCH_PREFIX = 'task:'

// The leading boundary is matched instead of looked behind, because iOS 16.4 is
// the deployment target and that is the first Safari with lookbehind support.
const TASK_NUMBER_SEARCH_TOKEN_REGEX = /(^|[\s+])task:#(\d+)(?=[\s+]|$)/gi

// "fenster task:#12 tuer" -> { taskNumberSegments: [ '#12' ], rest: 'fenster tuer' }
const splitOffTaskNumberTokens = searchString => {
  const taskNumberSegments = [ ...searchString.matchAll(TASK_NUMBER_SEARCH_TOKEN_REGEX) ]
    .map(match => `#${match[2]}`)

  return {
    taskNumberSegments,
    rest: searchString
      .replace(TASK_NUMBER_SEARCH_TOKEN_REGEX, '')
      .trim(),
  }
}

// A search index text as the lowercased segments the search matches against
const splitSearchIndex = text => {
  return text
    .toLowerCase()
    .split(constants.SEARCH_INDEX_SEPARATOR)
}

const containsAny = (textSegments, searchStrings) => {
  for (const searchString of searchStrings) {
    const hasMatch = textSegments.some(textSegment => textSegment.includes(searchString))

    if (hasMatch) {
      return true
    }
  }

  return false
}

// Every + separated part has to match, so these are the AND groups of a search.
// An empty search string has none of them and matches everything.
const getSearchStringParts = searchString => {
  return searchString
    .split('+')
    .map(part => part.trim())
    .filter(Boolean)
}

// textSegments (see splitSearchIndex) can be passed instead of text by a caller that caches them
const performSearch = ({ text, textSegments, searchString = '' }) => {
  let searchHit = true

  // First we split by +. For every part there must be a match!
  const searchStringParts = getSearchStringParts(searchString)

  if (searchStringParts.length === 0) {
    return searchHit
  }

  const searchIndexSegments = textSegments || splitSearchIndex(text)

  for (const searchStringPart of searchStringParts) {
    const searchStrings = getSplittedSearchString(searchStringPart)
    const hasMatch = containsAny(searchIndexSegments, searchStrings)

    // if no token in a + concatenated segment is found, then the search is a miss
    if (!hasMatch) {
      searchHit = false
    }
  }

  return searchHit
}

// The task search is the only one that knows the "task:#12" grammar. Everywhere
// else the same input stays plain text: a document named "task:#12 protokoll"
// has to remain findable, and a typed token must keep narrowing the search
// instead of being stripped out of it.
const performTaskSearch = ({ text, textSegments, searchString = '', exactSearchSegments = [] }) => {
  // Task number tokens are pulled out before the + split, so a token is a top
  // level alternative that never joins a + group. Inside a group it would be
  // ANDed with the rest and "task:#12 fenster+tuer" would stop finding task 12.
  const { taskNumberSegments, rest } = splitOffTaskNumberTokens(searchString)

  // Exact tokens do not inspect free-text segments: a title or comment can
  // legitimately equal "#12" without belonging to task #12.
  const hasTaskNumberMatch = taskNumberSegments.some(taskNumberSegment => exactSearchSegments.includes(taskNumberSegment))

  if (hasTaskNumberMatch) {
    return true
  }

  // Only task number tokens were searched for, and none of them matched above
  if (taskNumberSegments.length > 0 && getSearchStringParts(rest).length === 0) {
    return false
  }

  return performSearch({
    text,
    textSegments,
    searchString: rest,
  })
}

// "task:#12 fenster" -> { taskNumbers: [ 12 ], freeText: 'fenster' }
// Shares splitOffTaskNumberTokens with performTaskSearch, so what renders as a chip
// and what filters as a chip can never drift apart.
const parseSearchString = searchString => {
  const { taskNumberSegments, rest } = splitOffTaskNumberTokens(searchString)

  const taskNumbers = []

  for (const taskNumberSegment of taskNumberSegments) {
    const taskNumber = Number(taskNumberSegment.replace('#', ''))

    if (!taskNumbers.includes(taskNumber)) {
      taskNumbers.push(taskNumber)
    }
  }

  return {
    taskNumbers,
    freeText: rest,
  }
}

// The free text is passed through untouched so a trailing space survives while typing
const buildSearchString = ({ taskNumbers, freeText }) => {
  return [
    ...taskNumbers.map(taskNumber => `${TASK_NUMBER_SEARCH_PREFIX}#${taskNumber}`),
    freeText,
  ]
    .filter(Boolean)
    .join(' ')
}

export {
  getSplittedSearchString,
  getParticipantNameSearchSegments,
  performSearch,
  performTaskSearch,
  splitSearchIndex,
  parseSearchString,
  buildSearchString,
}
